import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, ChevronRight as Arrow } from 'lucide-react';
import { members, ALL_TIERS, ALL_STATUSES, formatINR, MemberStatus } from '../../data/members';
const PAGE_SIZE = 25;
function statusClasses(status: MemberStatus): string {
  switch (status) {
    case 'Active':
      return 'bg-green-50 text-green-700 border border-green-200';
    case 'Pending':
      return 'bg-orange-50 text-orange-700 border border-orange-200';
    case 'Suspended':
      return 'bg-red-50 text-red-700 border border-red-200';
    default:
      return 'bg-club-canvasAlt text-club-ink/70 border border-club-border';
  }
}
export function AdminMembers() {
  const [query, setQuery] = useState('');
  const [tier, setTier] = useState('All');
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter(m => {
      if (tier !== 'All' && m.tier !== tier) return false;
      if (status !== 'All' && m.status !== status) return false;
      if (q && !m.name.toLowerCase().includes(q) && !m.id.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [query, tier, status]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);
  // Reset to page 1 whenever a filter changes.
  const resetAnd = (fn: () => void) => {
    fn();
    setPage(1);
  };
  return <div className="max-w-7xl mx-auto space-y-6" data-id="element-502">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3" data-id="element-503">
        <div data-id="element-504">
          <span className="eyebrow block mb-1" data-id="element-505">Directory</span>
          <h1 className="text-3xl font-serif text-club-green" data-id="element-506">Members</h1>
        </div>
        <p className="text-sm text-club-ink/60" data-id="element-507">
          {filtered.length.toLocaleString('en-IN')} of{' '}
          {members.length.toLocaleString('en-IN')} members
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-club-border rounded-sm p-4 flex flex-col md:flex-row gap-3" data-id="element-508">
        <div className="relative flex-1" data-id="element-509">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-club-ink/40" data-id="element-510" />
          <input type="text" value={query} onChange={e => resetAnd(() => setQuery(e.target.value))} placeholder="Search by name or member ID…" className="w-full pl-10 pr-3 py-2.5 border border-club-border rounded-sm focus:outline-none focus:ring-1 focus:ring-club-gold focus:border-club-gold text-sm bg-club-canvasAlt/40" data-id="element-511" />
        </div>
        <select value={tier} onChange={e => resetAnd(() => setTier(e.target.value))} className="px-3 py-2.5 border border-club-border rounded-sm text-sm bg-white focus:outline-none focus:ring-1 focus:ring-club-gold" data-id="element-512">
          <option value="All" data-id="element-513">All Tiers</option>
          {ALL_TIERS.map(t => <option key={t} value={t} data-id="element-514">
              {t}
            </option>)}
        </select>
        <select value={status} onChange={e => resetAnd(() => setStatus(e.target.value))} className="px-3 py-2.5 border border-club-border rounded-sm text-sm bg-white focus:outline-none focus:ring-1 focus:ring-club-gold" data-id="element-515">
          <option value="All" data-id="element-516">All Statuses</option>
          {ALL_STATUSES.map(s => <option key={s} value={s} data-id="element-517">
              {s}
            </option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-club-border rounded-sm overflow-hidden" data-id="element-518">
        <div className="overflow-x-auto" data-id="element-519">
          <table className="w-full text-sm" data-id="element-520">
            <thead data-id="element-521">
              <tr className="bg-club-canvasAlt text-club-ink/60 text-left text-xs uppercase tracking-wider" data-id="element-522">
                <th className="px-4 py-3 font-medium" data-id="element-523">Member ID</th>
                <th className="px-4 py-3 font-medium" data-id="element-524">Name</th>
                <th className="px-4 py-3 font-medium" data-id="element-525">Tier</th>
                <th className="px-4 py-3 font-medium" data-id="element-526">Status</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-527">
                  Outstanding
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-528">Joined</th>
                <th className="px-4 py-3 font-medium" aria-hidden="true" data-id="element-529"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-club-border" data-id="element-530">
              {pageRows.map(m => <tr key={m.id} className="hover:bg-club-canvasAlt/40 transition-colors" data-id="element-531">
                  <td className="px-4 py-3 font-mono text-xs text-club-ink/70 whitespace-nowrap" data-id="element-532">
                    {m.id}
                  </td>
                  <td className="px-4 py-3" data-id="element-533">
                    <Link to={`/admin/members/${m.id}`} className="font-medium text-club-green hover:text-club-gold transition-colors" data-id="element-534">
                      {m.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-club-ink/70 whitespace-nowrap" data-id="element-535">
                    {m.tier}
                  </td>
                  <td className="px-4 py-3" data-id="element-536">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-medium ${statusClasses(m.status)}`} data-id="element-537">
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" data-id="element-538">
                    {m.outstandingBalance > 0 ? <span className="font-medium text-club-burgundy" data-id="element-539">
                        {formatINR(m.outstandingBalance)}
                      </span> : <span className="text-club-ink/40" data-id="element-540">—</span>}
                  </td>
                  <td className="px-4 py-3 text-club-ink/60 whitespace-nowrap" data-id="element-541">
                    {m.joinedDate}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-542">
                    <Link to={`/admin/members/${m.id}`} className="text-club-ink/30 hover:text-club-gold transition-colors inline-flex" aria-label={`View ${m.name}`} data-id="element-543">
                      <Arrow size={16} data-id="element-544" />
                    </Link>
                  </td>
                </tr>)}
              {pageRows.length === 0 && <tr data-id="element-545">
                  <td colSpan={7} className="px-4 py-12 text-center text-club-ink/50" data-id="element-546">
                    No members match your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-club-border bg-club-canvas/30" data-id="element-547">
          <p className="text-xs text-club-ink/60" data-id="element-548">
            {filtered.length === 0 ? 'Showing 0 of 0' : `Showing ${(start + 1).toLocaleString('en-IN')}–${Math.min(start + PAGE_SIZE, filtered.length).toLocaleString('en-IN')} of ${filtered.length.toLocaleString('en-IN')}`}
          </p>
          <div className="flex items-center gap-2" data-id="element-549">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-club-border rounded-sm disabled:opacity-40 disabled:cursor-not-allowed hover:border-club-gold transition-colors" data-id="element-550">
              <ChevronLeft size={16} data-id="element-551" /> Prev
            </button>
            <span className="text-sm text-club-ink/70 px-2" data-id="element-552">
              Page {currentPage} of {totalPages.toLocaleString('en-IN')}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-club-border rounded-sm disabled:opacity-40 disabled:cursor-not-allowed hover:border-club-gold transition-colors" data-id="element-553">
              Next <ChevronRight size={16} data-id="element-554" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}